import { computed, ref, watch } from "vue"
import { defineStore } from "pinia"
import { StorageSerializers, useLocalStorage } from "@vueuse/core"
import { UseRootStore, Permission } from "@/model/RootStore"
import { LayerType } from "@/model/DataModel"
import type { ComparableAnalysis, ComparableAnalysisInput, ComparableAnalysisSubjectInput } from "@/graphql"
import * as api from "@/utils/ApiClient"
import { comparableAnalysisFields } from "@/utils/comparableAnalysisFields"
import type { ComparableTransactionSummary } from "@/utils/comparableTransactionSummary"

export function normalizeComparableInscriptionNumber(value: string): string {
	return value.replace(/\s/g, "")
}

export function editableComparableAnalysisSubject(
	source: ComparableAnalysisSubjectInput,
	sensitive: boolean
): ComparableAnalysisSubjectInput {
	return Object.fromEntries(
		comparableAnalysisFields
			.filter(field => sensitive || !field.sensitive)
			.filter(field => Object.prototype.hasOwnProperty.call(source, field.key))
			.map(field => [field.key, source[field.key]])
	)
}

const DRAFT_STORAGE_KEY = "comparableAnalysisDraft"

type ComparableAnalysisDraft = {
	ownerId?: string
	input: ComparableAnalysisInput
	target?: { id: string; version: number; existingComparables: string[] }
	subjectParcelsText?: string
	summaries?: Record<string, ComparableTransactionSummary>
}

export const useComparableAnalysisStore = defineStore("comparable-analysis", () => {
	const root = UseRootStore()
	const saving = ref(false)
	const deleting = ref(false)
	const busy = computed(() => saving.value || deleting.value)
	const canRead = computed(
		() =>
			root.isUserLoggedIn &&
			root.currentUser.has(Permission.TransactionRead) &&
			(root.currentUser.has(Permission.ComparableAnalysisRead) ||
				root.currentUser.has(Permission.ComparableAnalysisWrite))
	)
	const canWrite = computed(() => canRead.value && root.currentUser.has(Permission.ComparableAnalysisWrite))
	const draft = useLocalStorage<ComparableAnalysisDraft | null>(DRAFT_STORAGE_KEY, null, {
		serializer: StorageSerializers.object,
		flush: "sync",
		listenToStorageChanges: false,
	})
	if (draft.value && (!canWrite.value || draft.value.ownerId !== root.currentUser.getInfo()?.id)) draft.value = null
	const canStart = computed(() => canWrite.value && !draft.value && !deleting.value)
	const canReadSensitive = computed(() => root.currentUser.has(Permission.TransactionSensitiveRead))
	watch(
		canReadSensitive,
		sensitive => {
			if (!sensitive && draft.value) {
				draft.value.input.subject = editableComparableAnalysisSubject(draft.value.input.subject, false)
			}
		},
		{ immediate: true, flush: "sync" }
	)

	function start(parcelIds: string[], name: string) {
		if (!canWrite.value || draft.value || deleting.value) return
		draft.value = {
			ownerId: root.currentUser.getInfo()?.id,
			input: {
				name: name.slice(0, 200),
				subjectParcelIds: [...new Set(parcelIds)],
				subject: {},
				comparableInscriptionNumbers: [],
			},
		}
	}

	function startEditing(analysis: ComparableAnalysis) {
		if (!canWrite.value || draft.value || busy.value) return
		draft.value = {
			ownerId: root.currentUser.getInfo()?.id,
			target: {
				id: analysis.id,
				version: analysis.version,
				existingComparables: analysis.comparables.map(value => value.inscriptionNumber),
			},
			input: {
				name: analysis.name,
				subjectParcelIds: [...analysis.subjectParcelIds],
				subject: JSON.parse(
					JSON.stringify(editableComparableAnalysisSubject(analysis.subject, canReadSensitive.value))
				),
				comparableInscriptionNumbers: analysis.comparables.map(value => value.inscriptionNumber),
			},
			subjectParcelsText: analysis.subjectParcelIds.join(", "),
			summaries: Object.fromEntries(
				analysis.comparables.map(value => [
					value.inscriptionNumber,
					{
						address: value.data?.address,
						salePrice: value.data?.salePrice,
					},
				])
			),
		}
	}

	function isExistingComparable(number: string) {
		return draft.value?.target?.existingComparables.includes(number) ?? false
	}

	function prepareMapSelection() {
		if (!canWrite.value || !draft.value || busy.value) return
		root.ensureLayerSelected(LayerType.Transaction)
	}

	function addTransaction(inscriptionNumber: string, summary?: ComparableTransactionSummary) {
		if (!draft.value || busy.value || !canWrite.value) return
		const number = normalizeComparableInscriptionNumber(inscriptionNumber)
		if (!/^\d+$/.test(number) || draft.value.input.comparableInscriptionNumbers.includes(number)) return
		if (draft.value.input.comparableInscriptionNumbers.length >= 50) return
		draft.value.input.comparableInscriptionNumbers.push(number)
		if (summary) draft.value.summaries = { ...draft.value.summaries, [number]: summary }
	}

	function removeTransaction(number: string) {
		if (!draft.value || busy.value || !canWrite.value || isExistingComparable(number)) return
		draft.value.input.comparableInscriptionNumbers = draft.value.input.comparableInscriptionNumbers.filter(
			value => value !== number
		)
		if (draft.value.summaries) {
			const { [number]: _removed, ...summaries } = draft.value.summaries
			draft.value.summaries = summaries
		}
	}

	async function saveDraft() {
		if (!draft.value || busy.value || !canWrite.value) return null
		const pendingDraft = draft.value
		const actorId = root.currentUser.getInfo()?.id
		const isCurrent = () => draft.value === pendingDraft && root.currentUser.getInfo()?.id === actorId && canWrite.value
		saving.value = true
		try {
			const input = {
				...pendingDraft.input,
				...(pendingDraft.subjectParcelsText !== undefined
					? {
							subjectParcelIds: pendingDraft.subjectParcelsText
								.split(",")
								.map(value => value.trim())
								.filter(Boolean),
						}
					: {}),
				subject: editableComparableAnalysisSubject(pendingDraft.input.subject, canReadSensitive.value),
			}
			const saved = pendingDraft.target
				? await api.updateComparableAnalysis(
						root.userApiToken,
						pendingDraft.target.id,
						pendingDraft.target.version,
						input
					)
				: await api.createComparableAnalysis(root.userApiToken, input)
			if (!isCurrent()) return null
			draft.value = null
			return saved
		} catch (failure) {
			if (!isCurrent()) return null
			throw failure
		} finally {
			saving.value = false
		}
	}

	function cancel() {
		if (!busy.value) draft.value = null
	}
	let accessRevision = 0
	watch(
		[() => root.currentUser.getInfo()?.id, () => root.isUserLoggedIn, canWrite],
		() => {
			accessRevision++
			deleting.value = false
		},
		{ flush: "sync" }
	)
	async function remove(id: string): Promise<boolean> {
		if (!canWrite.value || busy.value) return false
		const request = accessRevision
		deleting.value = true
		try {
			// Both true (deleted) and false (already absent) confirm that the analysis is gone.
			await api.deleteComparableAnalysis(root.userApiToken, id)
			if (request !== accessRevision) return false
			if (draft.value?.target?.id === id) draft.value = null
			return true
		} catch (failure) {
			if (request !== accessRevision) return false
			throw failure
		} finally {
			if (request === accessRevision) deleting.value = false
		}
	}
	watch(
		() => root.currentUser.getInfo()?.id,
		() => {
			draft.value = null
		}
	)
	watch(
		() => root.isUserLoggedIn,
		value => {
			if (!value) draft.value = null
		}
	)
	watch(canWrite, value => {
		if (!value) draft.value = null
	})

	return {
		draft,
		saving,
		deleting,
		canRead,
		canWrite,
		canStart,
		canReadSensitive,
		start,
		startEditing,
		isExistingComparable,
		prepareMapSelection,
		addTransaction,
		removeTransaction,
		saveDraft,
		remove,
		cancel,
		list: (limit = 25, offset = 0) => api.fetchComparableAnalyses(root.userApiToken, limit, offset),
		get: (id: string) => api.fetchComparableAnalysis(root.userApiToken, id),
		findTransactions: (inscriptionNumber: string) =>
			api.getTransactionsByInscriptionNumber(root.userApiToken, inscriptionNumber),
		update: (id: string, version: number, input: ComparableAnalysisInput) =>
			api.updateComparableAnalysis(root.userApiToken, id, version, {
				...input,
				subject: editableComparableAnalysisSubject(input.subject, canReadSensitive.value),
			}),
	}
})
